// Artificial sky brightness from the World Atlas of the Artificial Night Sky Brightness (Falchi et al.
// 2016, CC BY-NC 4.0), shipped as 5° PNG chunks at 30″ (scripts/build-lightpollution.py). The atlas uses
// 2014–15 satellite data, so values are indicative: many places have brightened since.

export interface SkyBrightness {
  /** Zenith brightness in magnitudes per square arcsecond (22 is a pristine sky). */
  sqm: number;
  /** Indicative Bortle class (1 excellent dark site … 9 inner city). */
  bortle: number;
  /** Artificial brightness as a fraction of the natural sky's. */
  ratio: number;
}

interface LpIndex { lat0: number; px: number; size: number; natural: number; chunks: string[] }

const BASE = `${import.meta.env.BASE_URL}lp/`;
let indexP: Promise<{ meta: LpIndex; have: Set<string> }> | null = null;
const chunks = new Map<string, Promise<Uint8Array | null>>();

function loadIndex() {
  indexP ??= fetch(`${BASE}index.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`lp index ${r.status}`);
      return r.json() as Promise<LpIndex>;
    })
    .then((meta) => ({ meta, have: new Set(meta.chunks) }))
    .catch((e) => {
      indexP = null;
      throw e;
    });
  return indexP;
}

async function decode(url: string, size: number): Promise<Uint8Array> {
  const blob = await (await fetch(url)).blob();
  const bmp = await createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(bmp, 0, 0);
  bmp.close?.();
  const rgba = g.getImageData(0, 0, size, size).data;
  const out = new Uint8Array(size * size);
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4];
  return out;
}

function loadChunk(key: string, size: number) {
  let p = chunks.get(key);
  if (!p) {
    p = decode(`${BASE}${key}.png`, size).catch(() => {
      chunks.delete(key);
      return null;
    });
    chunks.set(key, p);
    // Keep memory bounded: each chunk is 360 kB once decoded.
    if (chunks.size > 12) chunks.delete(chunks.keys().next().value!);
  }
  return p;
}

/** Bortle class from zenith brightness (the usual SQM ranges; indicative only). */
export function bortleFromSqm(sqm: number): number {
  const LIMITS: [number, number][] = [[21.99, 1], [21.89, 2], [21.69, 3], [20.49, 4], [19.5, 5], [18.94, 6], [18.38, 7], [17.8, 8]];
  for (const [lim, cls] of LIMITS) if (sqm >= lim) return cls;
  return 9;
}

export function sqmFromRatio(ratio: number, natural = 0.171168465): number {
  return Math.log10(((ratio + 1) * natural) / 108e6) / -0.4;
}

/** Sky brightness at a place, or null if the data cannot be loaded. */
export async function skyBrightnessAt(lat: number, lon: number): Promise<SkyBrightness | null> {
  try {
    const { meta, have } = await loadIndex();
    const row = Math.floor((meta.lat0 - lat) * meta.px);
    const col = Math.floor((((lon + 180) % 360) + 360) % 360 * meta.px);
    let v = 0;
    if (row >= 0) {
      const k = Math.floor(row / meta.size), j = Math.floor(col / meta.size);
      const key = `${k}_${j}`;
      if (have.has(key)) {
        const data = await loadChunk(key, meta.size);
        if (!data) return null;
        v = data[(row - k * meta.size) * meta.size + (col - j * meta.size)];
      }
    }
    const ratio = v ? Math.pow(10, v / 50 - 3) : 0;
    const sqm = sqmFromRatio(ratio, meta.natural);
    return { sqm, bortle: bortleFromSqm(sqm), ratio };
  } catch {
    return null;
  }
}
